/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {Dispatch, SetStateAction, useCallback, useRef, useState} from 'react';
import {getDevice} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, Device} from '@ion/core/src/types/entities';

// defines the return type of the useDeviceListSelection hook...
interface UseDeviceListSelection {
  formState: object;
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  setFormState: Function;
  setRemovedDevice: Function;
  setMovingToGroup: Function;
  onMoveDevice?: (group: Account) => void;
};

/**
 * Defining the useDeviceListSelection generic hook which can be utilized by the
 * DeviceList component of admin asset editors.
 *
 * ```typescript
 *
 * import useDeviceListSelection from './useDeviceListSelection';
 *
 * const {
 *   onDeleteSelectedDevice,
 *   onConfirmRemoveDevice,
 *   onCancelRemoveDevice,
 *   isRemoveDevice,
 *   deletedFormDevices
 * } = useDeviceListSelection({
 *   formState,
 *   selected,
 *   setSelected,
 *   setFormState,
 *   setRemovedDevice,
 *   setMovingToGroup,
 * });
 *
 * ```
 */
export const useDeviceListSelection = ({
  formState,
  selected,
  setSelected,
  setFormState,
  setRemovedDevice,
  setMovingToGroup,
  onMoveDevice
}: UseDeviceListSelection) => {
  const [isRemoveDevice, setIsRemoveDevice]         = useState<boolean>(false);
  const [deletedFormDevices, setDeletedFormDevices] = useState<Device>();
  const removedDeviceValueRef = useRef(null);
  const removedDevicesRef     = useRef([]);
  const movingToGroupRef      = useRef([]);

  const onDeleteSelectedDevice = useCallback((deviceValue) => () => {
    removedDeviceValueRef.current = deviceValue;
    const deletedFormDevices = formState?.devices?.value?.filter(({id}) =>
      id === deviceValue
    );
    setDeletedFormDevices(deletedFormDevices?.[0]);
    setIsRemoveDevice(true);
  }, [formState?.devices]);

  const onConfirmRemoveDevice = useCallback(async(group) => {
    const currentSelectedValues = selected.filter(({value}) =>
      value !== removedDeviceValueRef.current);
    setSelected(currentSelectedValues);
    const currentFormDevices = formState?.devices?.value?.filter(({id}) =>
      id !== removedDeviceValueRef.current
    );
    setFormState({devices: {name: 'devices', value: currentFormDevices}});
    const deviceData = deletedFormDevices &&
        await getDevice(entityId(deletedFormDevices.href || deletedFormDevices._self?.href));
    removedDevicesRef.current.push({
      isRemoved : true,
      device    : deviceData?.[0],
      group
    });
    onMoveDevice?.(group);
    movingToGroupRef.current.push(group);
    setRemovedDevice(removedDevicesRef.current);
    setMovingToGroup(movingToGroupRef.current);
    setIsRemoveDevice(false);
  }, [formState?.devices, selected, deletedFormDevices]);

  const onCancelRemoveDevice = useCallback(() => {
    setIsRemoveDevice(false);
  }, []);

  return {
    onDeleteSelectedDevice,
    onConfirmRemoveDevice,
    onCancelRemoveDevice,
    isRemoveDevice,
    deletedFormDevices
  };
};

export default useDeviceListSelection;
